import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Mountain, ChevronDown } from 'lucide-react';

const S  = '#1C1917';
const SW = 1.5;

// stroke-dashoffset draw-on animation
// base 0.5s delay so drawing starts after the flip completes
function draw(active, delay, length) {
  return {
    strokeDasharray: length,
    strokeDashoffset: active ? 0 : length,
    transition: active
      ? `stroke-dashoffset 0.6s ease ${0.5 + delay}s`
      : 'none',
  };
}

/* ── Illustrations ─────────────────────────────────────────── */

const AIIllustration = ({ active }) => (
  <svg viewBox="0 0 200 160" fill="none" className="w-full h-full p-4">
    {/* Faint cross connections */}
    <path d="M 100,42 L 130,126" stroke={S} strokeWidth={1} strokeLinecap="round" strokeOpacity="0.25" style={draw(active, 0.25, 92)} />
    <path d="M 100,42 L 70,126"  stroke={S} strokeWidth={1} strokeLinecap="round" strokeOpacity="0.25" style={draw(active, 0.28, 92)} />
    <path d="M 48,80 L 130,126"  stroke={S} strokeWidth={1} strokeLinecap="round" strokeOpacity="0.25" style={draw(active, 0.31, 112)} />
    <path d="M 152,80 L 70,126"  stroke={S} strokeWidth={1} strokeLinecap="round" strokeOpacity="0.25" style={draw(active, 0.34, 112)} />
    {/* Main edges */}
    <path d="M 100,42 L 152,80"  stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0,    62)} />
    <path d="M 100,42 L 48,80"   stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.05, 62)} />
    <path d="M 152,80 L 130,126" stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.1,  52)} />
    <path d="M 48,80  L 70,126"  stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.15, 52)} />
    <path d="M 130,126 L 70,126" stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.2,  60)} />
    <path d="M 48,80  L 152,80"  stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.22, 104)} />
    {/* Center hub spokes */}
    <path d="M 100,83 L 100,49"  stroke={S} strokeWidth={1}  strokeLinecap="round" strokeOpacity="0.4" style={draw(active, 0.38, 34)} />
    <path d="M 105,88 L 145,82"  stroke={S} strokeWidth={1}  strokeLinecap="round" strokeOpacity="0.4" style={draw(active, 0.4,  40)} />
    <path d="M 95,88  L 55,82"   stroke={S} strokeWidth={1}  strokeLinecap="round" strokeOpacity="0.4" style={draw(active, 0.4,  40)} />
    {/* Nodes */}
    <circle cx="100" cy="42"  r="7" stroke={S} strokeWidth={SW} style={draw(active, 0.45, 44)} />
    <circle cx="152" cy="80"  r="7" stroke={S} strokeWidth={SW} style={draw(active, 0.5,  44)} />
    <circle cx="130" cy="126" r="7" stroke={S} strokeWidth={SW} style={draw(active, 0.55, 44)} />
    <circle cx="70"  cy="126" r="7" stroke={S} strokeWidth={SW} style={draw(active, 0.6,  44)} />
    <circle cx="48"  cy="80"  r="7" stroke={S} strokeWidth={SW} style={draw(active, 0.65, 44)} />
    {/* Center node */}
    <circle cx="100" cy="88"  r="5" stroke={S} strokeWidth={SW} style={draw(active, 0.7,  32)} />
  </svg>
);

const TrekIllustration = ({ active }) => (
  <svg viewBox="0 0 200 160" fill="none" className="w-full h-full p-2">
    <path d="M 0,138 Q 35,122 58,132 Q 78,140 98,130 Q 138,115 200,122"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0, 220)} />
    <path d="M 88,128 L 148,32 L 200,128"
      stroke={S} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" style={draw(active, 0.15, 235)} />
    <path d="M 76,128 L 116,68 L 148,108"
      stroke={S} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" style={draw(active, 0.25, 148)} />
    <circle cx="42" cy="86" r="11"
      stroke={S} strokeWidth={SW} style={draw(active, 0.4, 70)} />
    <path d="M 42,97 L 39,130"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.5, 34)} />
    <path d="M 47,100 C 63,97 63,125 47,127"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.55, 54)} />
    <path d="M 53,116 L 66,142"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.6, 28)} />
    <path d="M 39,130 L 32,150"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.65, 21)} />
    <path d="M 39,130 L 48,148"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.65, 19)} />
    <path d="M 68,133 Q 84,148 80,156 Q 76,162 92,162"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.8, 58)} />
    <path d="M 28,151 Q 31,144 34,151 Q 37,144 40,151"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.9, 20)} />
  </svg>
);

const BangaloreIllustration = ({ active }) => (
  <svg viewBox="0 0 200 150" fill="none" className="w-full h-full p-2">
    {/* Ground + steps */}
    <path d="M 0,145 L 200,145"    stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0,    200)} />
    <path d="M 15,137 L 185,137"   stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.05, 170)} />
    <path d="M 28,129 L 172,129"   stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.1,  144)} />
    {/* Main facade */}
    <path d="M 28,129 L 28,76 L 172,76 L 172,129"
      stroke={S} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" style={draw(active, 0.15, 290)} />
    {/* Horizontal band */}
    <path d="M 28,94 L 172,94"     stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.25, 144)} />
    {/* Columns × 6 */}
    {[44, 63, 82, 118, 137, 156].map((x, i) => (
      <path key={i} d={`M ${x},129 L ${x},94`}
        stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.3 + i * 0.03, 35)} />
    ))}
    {/* Central arch */}
    <path d="M 88,129 Q 88,110 100,106 Q 112,110 112,129"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.5, 52)} />
    {/* Upper tier */}
    <path d="M 48,76 L 48,50 L 152,50 L 152,76"
      stroke={S} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" style={draw(active, 0.55, 188)} />
    {/* Upper columns × 4 */}
    {[66, 86, 114, 134].map((x, i) => (
      <path key={i} d={`M ${x},76 L ${x},50`}
        stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.62 + i * 0.03, 26)} />
    ))}
    {/* Main dome */}
    <path d="M 72,50 Q 72,20 100,15 Q 128,20 128,50"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.75, 92)} />
    {/* Side domes */}
    <path d="M 48,50 Q 48,38 59,34 Q 70,38 70,50"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.8, 44)} />
    <path d="M 130,50 Q 130,38 141,34 Q 152,38 152,50"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.8, 44)} />
    {/* Finials */}
    <circle cx="100" cy="15" r="3" stroke={S} strokeWidth={SW} style={draw(active, 0.9,  20)} />
    <circle cx="59"  cy="34" r="2" stroke={S} strokeWidth={SW} style={draw(active, 0.92, 13)} />
    <circle cx="141" cy="34" r="2" stroke={S} strokeWidth={SW} style={draw(active, 0.92, 13)} />
    {/* Flag */}
    <path d="M 100,12 L 100,4 L 109,7 L 100,10"
      stroke={S} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" style={draw(active, 0.96, 28)} />
  </svg>
);

const CookingIllustration = ({ active }) => (
  <svg viewBox="0 0 200 155" fill="none" className="w-full h-full p-2">
    <path d="M 58,80 L 58,122 Q 58,132 68,132 L 148,132 Q 158,132 158,122 L 158,80"
      stroke={S} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" style={draw(active, 0, 225)} />
    <path d="M 52,80 L 164,80"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.1, 112)} />
    <path d="M 58,88 L 22,88 L 22,102 L 58,102"
      stroke={S} strokeWidth={SW} strokeLinecap="round" strokeLinejoin="round" style={draw(active, 0.2, 84)} />
    <path d="M 66,98 Q 88,88 108,98 Q 128,108 150,98"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.35, 100)} />
    <path d="M 82,78 Q 76,65 82,53 Q 88,41 82,29"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.5, 58)} />
    <path d="M 105,76 Q 99,63 105,51 Q 111,39 105,27"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.65, 58)} />
    <path d="M 128,78 Q 122,65 128,53 Q 134,41 128,29"
      stroke={S} strokeWidth={SW} strokeLinecap="round" style={draw(active, 0.8, 58)} />
  </svg>
);

const TimelineBack = ({ active }) => {
  const stops = [
    { year: '2016', label: 'Agency\nwork' },
    { year: '2019', label: 'Healofy' },
    { year: '2021', label: 'Narayana\nHealth' },
  ];
  return (
    <div className="w-full h-full px-8 flex items-center">
      <div className="relative w-full flex items-start justify-between">
        {/* Connecting line */}
        <div className="absolute left-0 right-0 top-[9px] h-px bg-neutral-200" />
        {stops.map((stop, i) => (
          <motion.div
            key={i}
            className="flex flex-col items-center gap-2 relative z-10"
            initial={{ opacity: 0, y: 6 }}
            animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y: 6 }}
            transition={{ duration: 0.3, delay: active ? 0.55 + i * 0.1 : 0 }}
          >
            <div className={`w-[9px] h-[9px] rounded-full border-2 border-accent ${i === stops.length - 1 ? 'bg-accent' : 'bg-white'}`} />
            <p className="text-[10px] font-mono text-accent font-medium leading-none">{stop.year}</p>
            <p className="text-[9px] font-mono text-neutral-500 whitespace-pre-line text-center leading-tight">{stop.label}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

/* ── Flip Card ─────────────────────────────────────────────── */

const FlipCard = ({ front, back, className = '', frontClassName = 'bg-white border border-neutral-200' }) => {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className={`relative ${className}`}
      style={{ perspective: '800px' }}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      <div
        className="relative w-full h-full"
        style={{
          transformStyle: 'preserve-3d',
          transition: 'transform 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
          transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
        }}
      >
        <div
          className={`absolute inset-0 ${frontClassName} rounded-2xl p-5 flex flex-col justify-between`}
          style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
        >
          {front}
        </div>
        <div
          className="absolute inset-0 bg-white border border-neutral-200 rounded-2xl overflow-hidden"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          {back(flipped)}
        </div>
      </div>
    </div>
  );
};

/* ── Hero ──────────────────────────────────────────────────── */

const Hero = ({ profile }) => {
  const cellCls = "h-36 bg-white border border-neutral-200 rounded-2xl p-5 flex flex-col justify-between";

  return (
    <section className="py-20 relative min-h-[calc(100vh-4rem)] flex flex-col justify-center">
      <div className="container mx-auto px-6 md:px-12 max-w-6xl">
        <div className="flex flex-col lg:flex-row lg:items-center gap-12 lg:gap-20 lg:justify-between">

          {/* Left */}
          <div className="flex flex-col gap-5 items-start">
            <div className="w-16 h-16 rounded-full overflow-hidden bg-neutral-50">
              <img src="/avatar.png" alt="Sanket Daware" className="w-full h-full object-cover object-top" />
            </div>
            <h1 className="text-4xl md:text-5xl font-semibold text-neutral-900 leading-tight font-display">
              Designing AI products<br />
              that people trust.
            </h1>
            <p className="text-neutral-500 text-sm font-sans leading-relaxed max-w-md">
              Recently at Narayana Health's AI division, shipping clinical tools, a consumer health app, and analytics platforms across enterprise and B2C. My design process runs on{' '}
              <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}>Claude</span>,{' '}
              <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}>Cursor</span>, and{' '}
              <span style={{ textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: '3px' }}>Figma Make</span>{' '}
              — tools that collapse the gap between idea and tested prototype.
            </p>
          </div>

          {/* Right: Bento */}
          <div className="w-full lg:w-[340px] shrink-0">
            <div className="grid grid-cols-2 gap-2">

              {/* 4 AI Products */}
              <FlipCard
                className="h-36"
                front={
                  <>
                    <p className="text-3xl font-display font-semibold text-accent">4</p>
                    <div>
                      <p className="text-xs font-mono text-neutral-700">AI products</p>
                      <p className="text-xs font-mono text-neutral-500">enterprise + consumer</p>
                    </div>
                  </>
                }
                back={(active) => <AIIllustration active={active} />}
              />

              {/* Trek */}
              <FlipCard
                className="h-36"
                front={
                  <>
                    <Mountain className="w-5 h-5 text-accent" strokeWidth={1.5} />
                    <div>
                      <p className="text-xs font-mono text-neutral-700">Trek</p>
                      <p className="text-xs font-mono text-neutral-500">Himalayas + beyond</p>
                    </div>
                  </>
                }
                back={(active) => <TrekIllustration active={active} />}
              />

              {/* 10 years — full width */}
              <FlipCard
                className="col-span-2 h-24"
                frontClassName="bg-neutral-900 border-neutral-900"
                front={
                  <div className="flex items-end justify-between h-full">
                    <div>
                      <p className="text-4xl font-display font-semibold text-white leading-none mb-2">10 years</p>
                      <p className="text-xs font-mono text-amber-400">0→1, twice</p>
                    </div>
                    <p className="text-xs font-mono text-neutral-500 uppercase tracking-widest text-right leading-relaxed">
                      Product<br />Design
                    </p>
                  </div>
                }
                back={(active) => <TimelineBack active={active} />}
              />

              {/* Bangalore */}
              <FlipCard
                className="h-36"
                front={
                  <>
                    <MapPin className="w-5 h-5 text-accent" strokeWidth={1.5} />
                    <div>
                      <p className="text-xs font-mono text-neutral-700">Bangalore</p>
                      <p className="text-xs font-mono text-neutral-500">India</p>
                    </div>
                  </>
                }
                back={(active) => <BangaloreIllustration active={active} />}
              />

              {/* Cooking */}
              <FlipCard
                className="h-36"
                front={
                  <>
                    <span className="text-lg">🍳</span>
                    <div>
                      <p className="text-xs font-mono text-neutral-700">Indian cooking channel</p>
                      <p className="text-xs font-mono text-neutral-500">food & stories</p>
                    </div>
                  </>
                }
                back={(active) => <CookingIllustration active={active} />}
              />

            </div>
          </div>
        </div>
      </div>

      {/* Scroll nudge */}
      <motion.div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5"
        animate={{ y: [0, 5, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      >
        <span className="text-[9px] font-mono text-neutral-400 uppercase tracking-widest">Real world work</span>
        <ChevronDown className="w-3.5 h-3.5 text-neutral-400" strokeWidth={1.5} />
      </motion.div>
    </section>
  );
};

export default Hero;
